/* eslint-disable max-lines */
import React, { useEffect, useState, useCallback, useRef } from 'react'
import Head from 'next/head'
import { FaCompass, FaLocationArrow, FaMosque, FaMapMarkerAlt } from 'react-icons/fa'
import { usePrayerTimes } from '../components/Home/PrayerTimes/PrayingTimesContext'
import { useIsAndroidDevice } from '../hooks/useIsAndroidDevice'
import Layout from '../components/shared/Layout/Layout'
import SectionWrapper from '../components/common/SectionWrapper'
import LoadingSpinner from '../components/shared/Loading/Loading'
import * as adhan from 'adhan'
import dynamic from 'next/dynamic'
import type {
  CompassHeadingSource,
  CompassOrientationEvent,
} from '../helpers/compassHeading'
import {
  getCompassHeading,
  getCompassHeadingPriority,
  getScreenOrientationAngle,
  smoothCompassHeading,
} from '../helpers/compassHeading'

// Dynamically import the map component because react-leaflet only works on the client-side
const QiblaMap = dynamic(() => import('../components/QiblaMap'), {
  ssr: false,
  loading: () => (
    <div className="w-full h-[400px] flex items-center justify-center bg-brand-primary/5 rounded-2xl border border-brand-primary/20">
      <LoadingSpinner />
    </div>
  ),
})

export default function KibleBulucu() {
  const { selectedLocation } = usePrayerTimes()
  const isAndroidDevice = useIsAndroidDevice()
  const [qiblaDirection, setQiblaDirection] = useState<number | null>(null)
  const [heading, setHeading] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null)
  const [isCalibrating, setIsCalibrating] = useState(false)
  const [isLocating, setIsLocating] = useState(false)

  const lastUpdateRef = useRef<number>(0)
  const lastSourceUpdateRef = useRef<number>(0)
  const smoothedHeadingRef = useRef<number | null>(null)
  const headingSourceRef = useRef<CompassHeadingSource | null>(null)

  // Use the selected location from prayer times if available
  useEffect(() => {
    if (selectedLocation?.latitude && selectedLocation?.longitude) {
      setUserCoords({
        lat: selectedLocation.latitude,
        lng: selectedLocation.longitude,
      })
    }
  }, [selectedLocation])

  const calculateQibla = useCallback((lat: number, lng: number) => {
    const coords = new adhan.Coordinates(lat, lng)
    const direction = adhan.Qibla(coords)
    setQiblaDirection(direction)
  }, [])

  useEffect(() => {
    if (userCoords) {
      calculateQibla(userCoords.lat, userCoords.lng)
    }
  }, [userCoords, calculateQibla])

  const requestGeolocation = () => {
    setError(null)
    setIsLocating(true)
    if (!navigator.geolocation) {
      setError('Tarayıcınız konum servisini desteklemiyor.')
      setIsLocating(false)
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserCoords({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        })
        setIsLocating(false)
      },
      (err) => {
        console.error(err)
        setError('Konum alınamadı. Lütfen konum erişimine izin verin.')
        setIsLocating(false)
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    )
  }

  const handleOrientation = useCallback((event: CompassOrientationEvent) => {
    const reading = getCompassHeading(event, getScreenOrientationAngle())
    if (!reading) return

    const now = Date.now()
    const currentSource = headingSourceRef.current

    if (
      currentSource &&
      reading.source !== currentSource &&
      getCompassHeadingPriority(reading.source) <
        getCompassHeadingPriority(currentSource) &&
      now - lastSourceUpdateRef.current < 2000
    ) {
      return
    }

    headingSourceRef.current = reading.source
    lastSourceUpdateRef.current = now

    // Pusula sensörleri çok sık ve gürültülü veri gönderir; 10fps bu UI için yeterli.
    if (now - lastUpdateRef.current < 100) return

    const smoothedHeading = smoothCompassHeading(
      smoothedHeadingRef.current,
      reading.heading,
      reading.source === 'relative' ? 0.07 : 0.1,
    )

    smoothedHeadingRef.current = smoothedHeading
    setHeading(smoothedHeading)
    lastUpdateRef.current = now
  }, [])

  // Handle device orientation for compass
  useEffect(() => {
    const requestOrientationPermission = async () => {
      if (
        typeof DeviceOrientationEvent !== 'undefined' &&
        // @ts-ignore
        typeof DeviceOrientationEvent.requestPermission === 'function'
      ) {
        try {
          // @ts-ignore
          const permissionState = await DeviceOrientationEvent.requestPermission()
          if (permissionState === 'granted') {
            window.addEventListener('deviceorientationabsolute', handleOrientation, true)
            // Fallback for older devices/iOS
            window.addEventListener('deviceorientation', handleOrientation, true)
          } else {
            setError('Pusula için cihaz yönelimi izni reddedildi.')
          }
        } catch (err) {
          console.error(err)
        }
      } else {
        // Non-iOS 13+ devices
        window.addEventListener('deviceorientationabsolute', handleOrientation, true)
        window.addEventListener('deviceorientation', handleOrientation, true)
      }
    }

    // Attempt to auto-request if possible, but iOS requires user gesture
    // We'll add a manual calibration button for iOS
    if (
      typeof DeviceOrientationEvent !== 'undefined' &&
      // @ts-ignore
      typeof DeviceOrientationEvent.requestPermission === 'function'
    ) {
      // Do nothing automatically, wait for user click
      setIsCalibrating(true)
    } else {
      requestOrientationPermission()
    }

    return () => {
      window.removeEventListener('deviceorientationabsolute', handleOrientation, true)
      window.removeEventListener('deviceorientation', handleOrientation, true)
    }
  }, [handleOrientation])

  const handleCalibrationClick = async () => {
    if (
      typeof DeviceOrientationEvent !== 'undefined' &&
      // @ts-ignore
      typeof DeviceOrientationEvent.requestPermission === 'function'
    ) {
      try {
        // @ts-ignore
        const permissionState = await DeviceOrientationEvent.requestPermission()
        if (permissionState === 'granted') {
          window.addEventListener('deviceorientationabsolute', handleOrientation, true)
          window.addEventListener('deviceorientation', handleOrientation, true)
          setIsCalibrating(false)
        } else {
          setError('Pusula için cihaz yönelimi izni reddedildi.')
        }
      } catch (err) {
        console.error(err)
      }
    }
  }

  // Calculate rotation for the Qibla arrow
  // The compass face rotates based on heading (North is 0)
  // The arrow points to Qibla relative to North
  const qiblaRotation =
    qiblaDirection !== null && heading !== null
      ? qiblaDirection - heading
      : qiblaDirection !== null
        ? qiblaDirection
        : 0

  return (
    <Layout>
      <Head>
        <title>Kıble Bulucu | Pusula & Harita ile Gerçek Zamanlı Kıble Yönü</title>
        <meta
          name="description"
          content="Bulunduğunuz konuma göre en doğru Kıble yönünü anında bulun. Gerçek zamanlı pusula ve harita desteğiyle Kabe'nin yönünü hassas bir şekilde tespit edin."
        />
        <meta
          name="keywords"
          content="kıble bulucu, kıble yönü, kıble pusulası, kıbleyi bul, kabe yönü, qibla finder, namaz vakitleri, kıble haritası, gerçek zamanlı kıble, online kıble bulucu"
        />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Kıble Bulucu | Pusula & Harita ile Kıble Yönü"
        />
        <meta
          property="og:description"
          content="Bulunduğunuz konuma göre en doğru Kıble yönünü anında bulun. Gerçek zamanlı pusula ve harita desteğiyle Kabe'nin yönünü kolayca tespit edin."
        />
        <meta property="og:site_name" content="HayırHah" />
        <meta property="og:locale" content="tr_TR" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Kıble Bulucu | Pusula & Harita ile Kıble Yönü"
        />
        <meta
          name="twitter:description"
          content="Bulunduğunuz konuma göre en doğru Kıble yönünü anında bulun. Gerçek zamanlı pusula ve harita desteğiyle Kabe'nin yönünü kolayca tespit edin."
        />

        {/* JSON-LD Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'WebApplication',
              name: 'Kıble Bulucu',
              description:
                'Konum bazlı gerçek zamanlı pusula ve harita ile Kıble yönünü hassas şekilde bulan çevrimiçi uygulama.',
              applicationCategory: 'UtilitiesApplication',
              operatingSystem: 'All',
              inLanguage: 'tr',
              publisher: {
                '@type': 'Organization',
                name: 'Hayırhah',
              },
            }),
          }}
        />
      </Head>

      <div className="max-w-2xl mx-auto py-8">
        <h1 className="text-3xl font-black text-center mb-8 flex items-center justify-center gap-3">
          <FaCompass className="text-brand-primary" />
          Kıble Bulucu
        </h1>

        <SectionWrapper>
          <div className="flex flex-col items-center justify-center text-center">
            {/* Android-only notice: Apple rejects iOS apps that mention other platforms */}
            {isAndroidDevice && (
              <div className="bg-brand-primary/10 text-brand-primary border border-brand-primary/20 p-4 rounded-xl mb-6 text-sm text-left">
                <strong className="block mb-1">
                  Android Uygulama Kullanıcılarının Dikkatine:
                </strong>
                Kıble Bulma özelliği ile ilgili sorunlar yeni uygulama sürümüyle
                giderilmiştir. Eğer hala konum sorunu yaşıyorsanız lütfen uygulamanızı en
                son sürüme güncellediğinizden emin olun.
              </div>
            )}

            {!userCoords ? (
              <div className="mb-8 flex flex-col items-center">
                <p className="mb-4 text-lg font-bold">
                  Kıble yönünü hesaplamak için konuma ihtiyacımız var.
                </p>
                <button
                  onClick={requestGeolocation}
                  disabled={isLocating}
                  className="bg-brand-secondary text-white px-6 py-3 rounded-xl font-bold flex items-center justify-center gap-2 mx-auto hover:opacity-90 transition-all active:scale-95 shadow-lg shadow-brand-primary/20 disabled:opacity-75 disabled:cursor-not-allowed disabled:active:scale-100 min-w-[180px]"
                >
                  {isLocating ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Konum Bulunuyor...
                    </>
                  ) : (
                    <>
                      <FaLocationArrow />
                      Konumumu Bul
                    </>
                  )}
                </button>
                {selectedLocation && (
                  <p className="mt-4 text-sm opacity-70">
                    Veya Namaz Vakitleri widget'ından bir konum seçin.
                  </p>
                )}
              </div>
            ) : (
              <div className="w-full">
                {error && (
                  <div className="bg-danger-subtle text-danger p-4 rounded-xl mb-6 text-sm font-bold">
                    {error}
                  </div>
                )}

                {qiblaDirection !== null ? (
                  <div className="flex flex-col items-center">
                    <p className="text-lg font-bold mb-2">
                      Kıble Açısı:{' '}
                      <span className="text-brand-primary">
                        {qiblaDirection.toFixed(2)}°
                      </span>
                    </p>
                    <p className="text-sm opacity-70 mb-8 font-bold">
                      (Kuzeyden saat yönünde)
                    </p>

                    {isCalibrating && (
                      <button
                        onClick={handleCalibrationClick}
                        className="bg-brand-secondary text-white px-6 py-2 rounded-xl font-bold mb-8 shadow-lg shadow-brand-secondary/20"
                      >
                        Pusulayı Etkinleştir
                      </button>
                    )}

                    <div className="relative w-64 h-64 md:w-80 md:h-80 mx-auto bg-brand-primary/5 rounded-full border-4 border-brand-primary/20 flex items-center justify-center shadow-inner overflow-hidden">
                      {/* Compass marking (North) */}
                      <div
                        className="absolute inset-0 transition-transform duration-75 ease-linear flex items-center justify-center"
                        style={{
                          transform: `rotate(${heading !== null ? -heading : 0}deg)`,
                        }}
                      >
                        <div className="absolute top-2 left-1/2 -translate-x-1/2 font-black text-brand-primary text-xl bg-background/50 px-2 rounded-md">
                          Kuzey
                        </div>
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 font-bold opacity-50 bg-background/50 px-2 rounded-md">
                          G
                        </div>
                        <div className="absolute left-2 top-1/2 -translate-y-1/2 font-bold opacity-50 bg-background/50 px-2 rounded-md">
                          B
                        </div>
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 font-bold opacity-50 bg-background/50 px-2 rounded-md">
                          D
                        </div>
                      </div>

                      {/* Qibla Arrow Container */}
                      <div
                        className="absolute inset-0 transition-transform duration-75 ease-linear z-10 flex flex-col items-center"
                        style={{
                          transform: `rotate(${qiblaRotation}deg)`,
                        }}
                      >
                        <div className="w-1 bg-brand-secondary h-1/2 relative mt-4">
                          <div className="absolute top-0 left-1/2 -translate-x-1/2 -mt-2">
                            <FaLocationArrow className="text-3xl text-brand-secondary -rotate-45" />
                          </div>
                        </div>
                        <FaMosque className="text-4xl text-brand-secondary mt-auto mb-8 bg-background/80 p-2 rounded-full" />
                      </div>

                      {heading === null && !isCalibrating && (
                        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm rounded-full flex flex-col items-center justify-center p-4 text-center z-20">
                          <FaCompass className="text-4xl text-brand-primary/50 mb-2 animate-pulse" />
                          <p className="text-sm font-bold">Pusula bekleniyor...</p>
                          <p className="text-xs opacity-70 mt-1">
                            Cihazınızı yatay tutun ve hareket ettirin
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="w-full mt-12 mb-4">
                      <h2 className="text-2xl font-black mb-6 flex items-center justify-center gap-2">
                        <FaMapMarkerAlt className="text-brand-primary" />
                        Harita Görünümü
                      </h2>
                      <QiblaMap userCoords={userCoords} heading={heading} />
                    </div>
                  </div>
                ) : (
                  <LoadingSpinner />
                )}

                <div className="mt-8 flex justify-center">
                  <button
                    onClick={requestGeolocation}
                    disabled={isLocating}
                    className="text-sm font-bold text-brand-primary underline decoration-2 underline-offset-4 disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline flex items-center gap-2"
                  >
                    {isLocating && (
                      <div className="w-4 h-4 border-2 border-brand-primary/30 border-t-brand-primary rounded-full animate-spin" />
                    )}
                    {isLocating ? 'Konum Güncelleniyor...' : 'Konumumu Güncelle'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </SectionWrapper>

        {/* SEO Text Content */}
        <div className="mt-12 text-foreground/90">
          <SectionWrapper>
            <div className="p-2 md:p-8">
              <h2 className="text-2xl font-black mb-6 flex items-center gap-3">
                <FaCompass className="text-brand-primary" />
                Kıble Nasıl Bulunur?
              </h2>

              <div className="space-y-4 leading-relaxed">
                <h3 className="text-xl font-bold mt-8 mb-4">
                  Harita ile Manuel Doğrulama
                </h3>
                <p>
                  Pusula hassasiyeti cihazdan cihaza ve çevresel faktörlere göre
                  değişkenlik gösterebilir. En sağlıklı sonuç için harita görünümü
                  üzerinden bulunduğunuz konumu, çevrenizdeki sokakları ve binaları
                  referans alarak Kıble yönünü manuel olarak da teyit etmeniz önerilir.
                </p>
                <p>
                  <strong>Kıble Bulucu</strong> aracımız, cihazınızın konum (GPS)
                  sensörlerini ve dahili pusulasını kullanarak size en doğru{' '}
                  <strong>Kıble yönünü</strong> anında gösterir. Namaz kıldığımız yön olan
                  Kabe-i Muazzama'nın (Mekke) açısı, bulunduğunuz dünya üzerindeki konuma
                  göre matematiksel algoritmalarla hesaplanır.
                </p>
                <h3 className="text-xl font-bold mt-8 mb-4">
                  En Doğru Sonuç İçin İpuçları:
                </h3>
                <ul className="list-disc pl-5 space-y-3">
                  <li>
                    <strong className="text-brand-primary">Konum İzni Verin:</strong>{' '}
                    Tarayıcınızın veya uygulamamızın konum servislerine erişimine izin
                    verdiğinizden emin olun. Hassas konum, Kabe açısının doğru
                    hesaplanması için kritiktir.
                  </li>
                  <li>
                    <strong className="text-brand-primary">
                      Cihazınızı Kalibre Edin:
                    </strong>{' '}
                    Telefonunuzu yere paralel (yatay) olarak tutun. Yön sensörlerinin
                    (pusulanın) doğru çalışması için cihazınızı havada 8 çizecek şekilde
                    birkaç saniye hareket ettirip kalibre edin.
                  </li>
                  <li>
                    <strong className="text-brand-primary">
                      Manyetik Alanlardan Uzak Durun:
                    </strong>{' '}
                    Pusulanın sapmaması ve doğru ölçüm almak için hoparlör, televizyon,
                    mikrodalga, büyük metal eşyalar veya diğer güçlü manyetik alan yayan
                    objelerden belli bir mesafe uzak durun.
                  </li>
                </ul>
                <p className="mt-8 text-sm opacity-80 italic border-l-4 border-brand-primary pl-4 py-1">
                  Sistemimiz gelişmiş <strong>Adhan</strong> astronomik hesaplama
                  kütüphanesi algoritmalarını ve{' '}
                  <strong>gerçek zamanlı Leaflet harita altyapısını</strong> kullanarak
                  size en güvenilir namaz yönü (Kıble) hizmetini sunmayı amaçlamaktadır.
                </p>
              </div>
            </div>
          </SectionWrapper>
        </div>
      </div>
    </Layout>
  )
}
